import type { ReactElement } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import { Box, Button, Drawer, IconButton, Typography } from '@mui/material'
import type { UserNotification } from '../../api/contracts'
import type { NotificationsFeedState } from '../../hooks/useNotificationsFeed'
import { useIsPhoneLayout } from '../../hooks/useViewport'
import BottomSheet from '../BottomSheet'
import CountPill from '../CountPill'
import NotificationsFeedPanel from './NotificationsFeedPanel'
import { NOTIFICATIONS_TEXT } from './notificationsText'

export interface NotificationsDrawerProps {
  open: boolean
  onClose: () => void
  unreadCount: number
  feed: NotificationsFeedState
  onOpenNotification: (notification: UserNotification) => void
  onMarkAllRead: () => void
}

const TITLE_ID = 'notifications-panel-title'

function UnreadPill({ count }: { count: number }): ReactElement | null {
  if (count <= 0) {
    return null
  }
  return (
    <Box component="span" data-testid="notifications-unread-pill" sx={{ display: 'inline-flex' }}>
      <CountPill count={count} tone="unread" size="tab" />
    </Box>
  )
}

/** Notifications feed: a right-side drawer on desktop and tablet, a full-height bottom sheet on the phone. */
export default function NotificationsDrawer({
  open,
  onClose,
  unreadCount,
  feed,
  onOpenNotification,
  onMarkAllRead
}: NotificationsDrawerProps): ReactElement {
  const isPhone = useIsPhoneLayout()

  const markAllButton = (
    <Button
      variant="text"
      size="small"
      onClick={onMarkAllRead}
      disabled={unreadCount === 0 || feed.isMarkingAll}
      sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, color: '#1a56c4', minWidth: 0, whiteSpace: 'nowrap' }}
    >
      {NOTIFICATIONS_TEXT.markAllRead}
    </Button>
  )

  const closeButton = (
    <IconButton
      aria-label={NOTIFICATIONS_TEXT.close}
      onClick={onClose}
      sx={{ width: 28, height: 28, borderRadius: '8px', color: '#5b6572' }}
    >
      <CloseIcon sx={{ fontSize: 18 }} />
    </IconButton>
  )

  const panel = (
    <NotificationsFeedPanel feed={feed} unreadCount={unreadCount} onOpenNotification={onOpenNotification} />
  )

  if (isPhone) {
    return (
      <BottomSheet
        open={open}
        onClose={onClose}
        titleId={TITLE_ID}
        fullHeight
        title={(
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
            {NOTIFICATIONS_TEXT.title}
            <UnreadPill count={unreadCount} />
          </Box>
        )}
        headerAction={(
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {markAllButton}
            {closeButton}
          </Box>
        )}
      >
        {panel}
      </BottomSheet>
    )
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: false }}
      slotProps={{
        paper: {
          'aria-labelledby': TITLE_ID,
          sx: {
            width: 420,
            maxWidth: '100vw',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '-12px 0 32px rgba(21,32,43,.12)'
          }
        },
        backdrop: { sx: { backgroundColor: 'rgba(21,32,43,.28)' } }
      }}
    >
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: '10px', px: 2, py: '12px', borderBottom: '1px solid #e6eaf1', flexShrink: 0 }}
      >
        <Typography id={TITLE_ID} component="h2" sx={{ fontSize: 15, fontWeight: 700, color: '#15202b' }}>
          {NOTIFICATIONS_TEXT.title}
        </Typography>
        <UnreadPill count={unreadCount} />
        <Box sx={{ flex: 1 }} />
        {markAllButton}
        {closeButton}
      </Box>
      {panel}
    </Drawer>
  )
}
